import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Page, PageHeader } from '@/components/shell'
import { api, type TelegramStatus } from '@/lib/api'

import { BotConfig, BotConfigSkeleton, StatusBadge } from '@/features/telegram/bot-config'
import { BroadcastHistory } from '@/features/telegram/broadcast-history'
import { ChatList } from '@/features/telegram/chat-list'
import { InteractionHistory } from '@/features/telegram/interaction-history'
import { TemplateEditor } from '@/features/telegram/template-editor'

export const Route = createFileRoute('/dashboard/telegram')({
  component: TelegramPage,
})

/**
 * Telegram 群通知。
 *
 * 五种广播：新币创建、毕业发射（扫 Launch 表，一个币各一次），以及 2026-09-10 加的
 * 大额买入（按笔，只报买入）、资金流摘要（按窗口）、里程碑（按节点）。事实源都是
 * ignix 库（只读），后台按自己的名单、阈值与模板往群里发；CA 查询卡按提问所在话题的语言回。
 *
 * 三件运营上容易搞混的事，界面里都写清楚了：
 *   · 群是**被动发现**的——Telegram 没有「列出我加入了哪些群」的接口；
 *   · 白名单模式下「未设置」等于不推，全部模式下「未设置」等于推；
 *   · 启用只对启用之后的新事件生效，历史代币不会补推。
 */
function TelegramPage() {
  const [tab, setTab] = useState('config')

  const { data: status, isLoading } = useQuery({
    queryKey: ['telegram', 'config'],
    queryFn: () => api.get<TelegramStatus>('/telegram/config'),
  })

  return (
    <Page>
      <PageHeader
        title="Telegram通知"
        description="新币创建、毕业发射、大额买入、资金流摘要与里程碑的 Telegram 群广播，以及群内 CA 查询卡。同一件事只会发一次——去重靠数据库唯一键，主站重建索引也不会重复推送。"
        actions={status ? <StatusBadge status={status} /> : null}
      />

      {isLoading || !status ? (
        <BotConfigSkeleton />
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="config">配置</TabsTrigger>
            <TabsTrigger value="chats">群（{status.chatCount.active}）</TabsTrigger>
            <TabsTrigger value="templates">消息模板</TabsTrigger>
            <TabsTrigger value="history">广播历史</TabsTrigger>
            <TabsTrigger value="interactions">交互记录</TabsTrigger>
          </TabsList>

          <TabsContent value="config">
            <BotConfig status={status} />
          </TabsContent>

          <TabsContent value="chats">
            <ChatList status={status} />
          </TabsContent>

          <TabsContent value="templates">
            <TemplateEditor status={status} />
          </TabsContent>

          <TabsContent value="history">
            <BroadcastHistory />
          </TabsContent>

          <TabsContent value="interactions">
            <InteractionHistory />
          </TabsContent>
        </Tabs>
      )}
    </Page>
  )
}
